import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Award, Users, CheckCircle, Target, Heart, Leaf, Briefcase, Shield, UtensilsCrossed, HeartPulse, Factory, GraduationCap, Building, Landmark } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { FORMATTED_STATS } from '../constants/siteStats';

const About = () => {
  const [team, setTeam] = useState([]);

  // Calculate years as Extreme Clean Australia (brand started 2006, business started 1999)
  const brandFoundingYear = 2006;
  const currentYear = new Date().getFullYear();
  const yearsExperience = currentYear - brandFoundingYear;

  // Stats - using centralised constants
  const stats = [
    { icon: Award, value: `${yearsExperience}+`, label: 'Years Experience' },
    { icon: Users, value: FORMATTED_STATS.activeClients, label: 'Active Clients' },
    { icon: Briefcase, value: FORMATTED_STATS.jobsCompletedThisYear, label: 'Jobs Completed This Year' }
  ];

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamRes = await axios.get(`${API}/team`);
        if (teamRes.data && teamRes.data.length > 0) {
          setTeam(teamRes.data);
        }
      } catch (error) {
        console.error('Error fetching team:', error);
      }
    };
    fetchTeam();
  }, []);

  const values = [
    { icon: Target, title: 'Excellence', desc: 'We never settle for less than perfect. Every clean meets our rigorous standards.' },
    { icon: Heart, title: 'Integrity', desc: 'Honest pricing, reliable service, and respect for your property.' },
    { icon: Leaf, title: 'Sustainability', desc: 'Eco-friendly products and practices that protect people and planet.' },
    { icon: Users, title: 'Teamwork', desc: 'Our success is built on collaboration and mutual respect.' }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/about" />
        <title>About Us | Extreme Clean Australia</title>
      </Helmet>
      <div className="pt-24 lg:pt-44" data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center" data-testid="about-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1718152421680-d1580e843cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwY2xlYW5pbmclMjBjcmV3JTIwY29tbWVyY2lhbHxlbnwwfHx8fDE3ODg5NDg5ODJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Industrial floor cleaning in progress"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#273268] bg-opacity-80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-4">About Us</p>
          <h1 className="font-heading font-bold text-5xl md:text-6xl leading-snug text-white mb-6 max-w-3xl">
            Setting the Standard Since 2006.
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl">
            For over 20 years, Extreme Clean Australia has cleaned the kitchens behind some of WA's biggest 
            food brands, McDonald's, KFC, and Domino's among them, built on a reputation for showing up, 
            doing the job properly, and standing behind the work with real compliance documentation.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#3B82F6] py-8" data-testid="about-stats">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="flex items-center justify-center gap-4">
                <stat.icon size={32} className="text-white opacity-80" />
                <div>
                  <p className="font-mono text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-white text-opacity-80 text-xs uppercase tracking-wider">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Our Story</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl mb-6">
                From Humble Beginnings to Industry Leaders
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  It started in 1999 with one Domino's oven a night, fully stripped down, high-pressure 
                  cleaned from the back of a truck, and put back together spotless. That kind of hands-on, 
                  done-properly approach never changed, it just scaled up. The business became Extreme Clean Australia in 2006, and the same hands-on approach has carried through ever since.
                </p>
                <p>
                  By 2015 we held the full KFC exhaust contract for Western Australia, 60+ stores, and 
                  grew McDonald's canopy and rooftop fan cleaning from 3 sites to 30+. Along the way we picked 
                  up City of Canning, The Coffee Club, Baptist Care, and The Cray, and today we're restructuring 
                  toward ISO compliance and government and mining contracts, the same commitment to doing it 
                  right, just at a bigger scale.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1663790776711-9283bf614ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwcmVzdGF1cmFudCUyMGtpdGNoZW4lMjBzdGFpbmxlc3MlMjBzdGVlbHxlbnwwfHx8fDE3ODg5NDg5OTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Commercial kitchen with stainless steel equipment"
                className="w-full"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-[#3B82F6] p-6 flex justify-between items-center">
                <div>
                  <p className="text-white font-heading font-bold text-lg">Industry Certified</p>
                  <p className="text-blue-100 text-sm">Healthcare & Commercial</p>
                </div>
                <CheckCircle size={40} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Work With */}
      <section className="py-24 bg-gray-50" data-testid="industries">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Our Expertise</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl">Industries We Work With</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 - Industrial & Warehouse (Featured) */}
            <Link to="/services" className="block bg-[#273268] p-8 shadow-lg lg:scale-105 lg:-my-4 cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-[1.07] lg:hover:scale-[1.08]" data-testid="industry-industrial">
              <div className="w-14 h-14 bg-[#3B82F6] mb-6 flex items-center justify-center">
                <Factory size={28} className="text-white" />
              </div>
              <div className="inline-block bg-[#3B82F6] text-white text-xs font-mono uppercase tracking-wider px-3 py-1 mb-4">
                Core Focus
              </div>
              <h3 className="font-heading font-bold text-xl mb-3 text-white">Industrial & Warehouse</h3>
              <p className="text-gray-300">
                This is where we're putting our focus. From end-of-lease warehouse turnovers, rotary concrete 
                floor cleaning, and paint removal, to high-pressure cleaning, commercial carpet, toilet and 
                window detailing, and carpark cleans, including ongoing work with local government. This is 
                the direction we're actively building toward with government and mining contracts, and it's 
                our core focus going forward.
              </p>
            </Link>

            {/* Card 2 - Aged Care & Healthcare */}
            <Link to="/industries/aged-care-healthcare-cleaning-perth" className="block bg-white p-8 shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1" data-testid="industry-healthcare">
              <div className="w-14 h-14 bg-blue-50 mb-6 flex items-center justify-center">
                <HeartPulse size={28} className="text-[#3B82F6]" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Aged Care & Healthcare Facilities</h3>
              <p className="text-gray-600">
                Kitchen hygiene cleaning for Baptist Care's facilities from Perth to Albany — built to the standard vulnerable residents require.
              </p>
            </Link>

            {/* Card 3 - Food Service & Hospitality */}
            <Link to="/services/commercial-kitchen-cleaning-perth" className="block bg-white p-8 shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1" data-testid="industry-food">
              <div className="w-14 h-14 bg-blue-50 mb-6 flex items-center justify-center">
                <UtensilsCrossed size={28} className="text-[#3B82F6]" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Food Service & Hospitality</h3>
              <p className="text-gray-600">
                McDonald's, KFC, Domino's, and commercial kitchens across WA trust us with the exhaust systems 
                and kitchens behind some of the busiest food service sites in the state.
              </p>
            </Link>

            {/* Card 4 - Schools & Educational Facilities */}
            <Link to="/industries/schools-educational-facilities-perth" className="block bg-white p-8 shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1" data-testid="industry-schools">
              <div className="w-14 h-14 bg-blue-50 mb-6 flex items-center justify-center">
                <GraduationCap size={28} className="text-[#3B82F6]" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Schools & Educational Facilities</h3>
              <p className="text-gray-600">
                Deep cleaning scheduled around school holidays — canteens, sports courts, carpets, windows and outdoor grounds.
              </p>
            </Link>

            {/* Card 5 - Strata & Apartment Complexes */}
            <Link to="/industries/strata-apartment-cleaning-perth" className="block bg-white p-8 shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1" data-testid="industry-strata">
              <div className="w-14 h-14 bg-blue-50 mb-6 flex items-center justify-center">
                <Building size={28} className="text-[#3B82F6]" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Strata & Apartment Complexes</h3>
              <p className="text-gray-600">
                Scheduled maintenance and ad-hoc call-outs for strata buildings — clear communication with strata and facilities managers.
              </p>
            </Link>

            {/* Card 6 - Government Buildings */}
            <Link to="/industries/government-building-cleaning-perth" className="block bg-white p-8 shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1" data-testid="industry-government">
              <div className="w-14 h-14 bg-blue-50 mb-6 flex items-center justify-center">
                <Landmark size={28} className="text-[#3B82F6]" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Government Buildings</h3>
              <p className="text-gray-600">
                Reliable, scheduled cleaning for government and council facilities — kitchens, canopies, sports centres and common areas.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50" data-testid="values">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Our Values</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl">What Drives Us</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-${value.title.toLowerCase()}`}>
                <div className="w-16 h-16 bg-blue-50 mx-auto mb-6 flex items-center justify-center">
                  <value.icon size={32} className="text-[#3B82F6]" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team - Only show if team members exist */}
      {team.length > 0 && (
        <section className="py-24 bg-[#273268]" data-testid="team">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Leadership</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white">Meet Our Team</h2>
            </div>

            <div className={`grid gap-8 max-w-4xl mx-auto ${team.length === 1 ? 'md:grid-cols-1 max-w-xs' : team.length === 2 ? 'md:grid-cols-2 max-w-2xl' : 'md:grid-cols-3'}`}>
              {team.map((member, index) => (
                <div key={member.id || index} className="group text-center" data-testid={`team-member-${index}`}>
                  <div className="w-32 h-32 bg-gray-800 mx-auto mb-4 flex items-center justify-center overflow-hidden">
                    {member.image ? (
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <Users size={48} className="text-gray-600" />
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white">{member.name}</h3>
                  <p className="text-gray-400 text-sm">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Safety & Compliance */}
      <section className="py-24" data-testid="certifications">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Safety & Compliance</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl mb-6">
                Safety & Compliance
              </h2>
              <p className="text-gray-600 mb-8">
                Working across commercial kitchens, industrial sites, and government facilities means working 
                within someone else's safety requirements, not just our own. Our team follows site-specific 
                safety procedures and provides the documentation clients and site managers actually need, 
                before, during, and after the job.
              </p>
              
              <ul className="space-y-4">
                {[
                  'AS 1851:2012 compliant kitchen exhaust cleaning, including grease filter exchange, with before and after photos and a certificate of completion for every job',
                  '$20 million public liability insurance',
                  '$50 million Workers Compensation coverage',
                  'Safe Work Method Statements (SWMS) provided where required',
                  'Job Safety Analysis (JSA) where required',
                  'Safety Data Sheets (SDS) for all cleaning chemicals used',
                  'All equipment tested and tagged'
                ].map((cert, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#3B82F6] mt-1 flex-shrink-0" />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-8 text-center">
                <Shield size={48} className="text-[#3B82F6] mx-auto mb-4" />
                <p className="font-heading font-bold text-2xl">$20M</p>
                <p className="text-sm text-gray-600">Public Liability</p>
              </div>
              <div className="bg-gray-50 p-8 text-center">
                <Shield size={48} className="text-[#3B82F6] mx-auto mb-4" />
                <p className="font-heading font-bold text-2xl">$50M</p>
                <p className="text-sm text-gray-600">Workers Compensation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3B82F6]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
            Join Our Growing Client Base
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Experience the Extreme Clean difference. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
            >
              Contact Us
            </a>
            <a 
              href="tel:0861875523"
              className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
            >
              08 6187 5523
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
